import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import PageLayout from "@/components/feature/PageLayout";
import NewsCard from "@/components/feature/NewsCard";
import StatusBlock from "@/components/feature/StatusBlock";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import { hotSearches, newsList } from "@/mocks/news";
import { sortedByHeat, searchNews } from "@/utils/news";

export default function SearchPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const query = (params.get("q") ?? "").trim();
  const [keyword, setKeyword] = useState(query);
  const [loading, setLoading] = useState(false);

  useDocumentMeta({
    title: query ? `“${query}”的搜索结果 - 爆料网` : "搜索爆料 - 爆料网",
    description: query
      ? `爆料网中与“${query}”相关的爆料内容，共找到多条结果，按发布时间展示。`
      : "输入关键词，搜索爆料网收录的社会新闻与娱乐八卦内容。",
    keywords: `${query || "搜索"},爆料网,最新爆料,热点资讯`,
  });

  useEffect(() => {
    setKeyword(query);
    setLoading(true);
    const timer = window.setTimeout(() => setLoading(false), 320);
    return () => window.clearTimeout(timer);
  }, [query]);

  const results = useMemo(() => (query ? searchNews(query) : []), [query]);
  const recommended = useMemo(() => sortedByHeat(newsList).slice(0, 3), []);

  const submit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const value = keyword.trim();
    navigate(value ? `/search?q=${encodeURIComponent(value)}` : "/search");
  };

  const goHot = (word: string) => {
    setKeyword(word);
    navigate(`/search?q=${encodeURIComponent(word)}`);
  };

  return (
    <PageLayout>
      <div className="mx-auto max-w-[1280px] px-4 md:px-6 pt-5 md:pt-7">
        <nav className="flex items-center gap-1.5 text-xs text-foreground-500 flex-wrap">
          <Link to="/" className="hover:text-primary-600 transition-colors cursor-pointer">
            首页
          </Link>
          <i className="ri-arrow-right-s-line text-foreground-300" />
          <span className="text-foreground-800">搜索结果</span>
        </nav>

        <header className="mt-4 rounded-lg border border-background-200/80 bg-background-100 px-5 md:px-7 py-6">
          <h1 className="text-2xl md:text-[26px] font-black text-foreground-950">
            {query ? (
              <>
                搜索“<span className="text-primary-600">{query}</span>”
              </>
            ) : (
              "搜索爆料"
            )}
          </h1>
          <p className="mt-2 text-sm text-foreground-500">
            支持按标题、摘要与正文关键词检索，结果按发布时间排序。
          </p>
          <form onSubmit={submit} className="mt-4 flex flex-col sm:flex-row gap-2 max-w-[640px]">
            <div className="relative flex-1">
              <i className="ri-search-line absolute left-3.5 top-1/2 -translate-y-1/2 text-foreground-400" />
              <input
                value={keyword}
                onChange={(event) => setKeyword(event.target.value)}
                placeholder="输入关键词，例如：地铁、婚礼、合约"
                aria-label="搜索关键词"
                className="w-full h-11 pl-10 pr-3 rounded-md bg-background-50 border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400"
              />
            </div>
            <button
              type="submit"
              className="h-11 px-6 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              搜索
            </button>
          </form>
        </header>

        <div className="mt-6 flex flex-col lg:flex-row gap-6 lg:gap-7 items-start">
          <section className="w-full lg:flex-1 min-w-0">
            {query && (
              <div className="flex flex-wrap items-center justify-between gap-2 pb-3 border-b border-background-200">
                <h2 className="text-base font-bold text-foreground-950">
                  搜索结果
                  <span className="ml-2 font-normal text-sm text-foreground-500">
                    共 {results.length} 条
                  </span>
                </h2>
                <span className="text-xs text-foreground-400">关键词：{query}</span>
              </div>
            )}

            {!query ? (
              <div className="mt-4">
                <StatusBlock
                  icon="ri-search-line"
                  title="输入关键词开始搜索"
                  description="可以试试下面这些正在被关注的词，或者直接回到首页看看最新爆料。"
                  actionLabel="返回首页"
                  onAction={() => navigate("/")}
                />
                <div className="mt-6">
                  <h2 className="text-sm font-bold text-foreground-900">热门搜索</h2>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {hotSearches.map((word) => (
                      <button
                        key={word}
                        type="button"
                        onClick={() => goHot(word)}
                        className="h-8 px-3 rounded-full bg-background-100 border border-background-200 text-[13px] text-foreground-700 hover:border-primary-300 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                      >
                        {word}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            ) : loading ? (
              <div className="mt-6 flex flex-col items-center justify-center py-20">
                <span className="w-9 h-9 rounded-full border-2 border-primary-200 border-t-primary-500 animate-spin" />
                <p className="mt-3 text-sm text-foreground-500">正在搜索相关爆料…</p>
              </div>
            ) : results.length === 0 ? (
              <div className="mt-4">
                <StatusBlock
                  icon="ri-emotion-normal-line"
                  title={`没有找到与“${query}”相关的爆料`}
                  description="换个更短的关键词试试，比如只输入事件中的地点、机构或人物称呼。"
                  actionLabel="返回首页"
                  onAction={() => navigate("/")}
                  secondaryLabel="清空关键词"
                  onSecondary={() => {
                    setKeyword("");
                    navigate("/search");
                  }}
                />
                <div className="mt-6 rounded-lg border border-background-200/80 bg-background-50 p-4">
                  <h2 className="text-sm font-bold text-foreground-900">大家都在搜</h2>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {hotSearches.map((word) => (
                      <button
                        key={word}
                        type="button"
                        onClick={() => goHot(word)}
                        className="h-8 px-3 rounded-full bg-background-100 text-[13px] text-foreground-700 hover:bg-primary-50 hover:text-primary-700 transition-colors cursor-pointer whitespace-nowrap"
                      >
                        {word}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <ul className="mt-4 space-y-3">
                {results.map((item) => (
                  <li key={item.id}>
                    <NewsCard item={item} />
                  </li>
                ))}
              </ul>
            )}
          </section>

          <aside className="w-full lg:w-[320px] shrink-0 space-y-5">
            <section className="rounded-lg border border-background-200/80 bg-background-50 p-4">
              <h2 className="text-[15px] font-bold text-foreground-950 flex items-center gap-2">
                <i className="ri-fire-line text-primary-500" />
                热门搜索
              </h2>
              <div className="mt-3 flex flex-wrap gap-2">
                {hotSearches.map((word) => (
                  <button
                    key={word}
                    type="button"
                    onClick={() => goHot(word)}
                    className="h-8 px-3 rounded-full bg-background-100 text-[13px] text-foreground-700 hover:bg-primary-50 hover:text-primary-700 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    {word}
                  </button>
                ))}
              </div>
            </section>

            <section className="rounded-lg border border-background-200/80 bg-background-50 overflow-hidden">
              <div className="px-4 h-12 flex items-center justify-between border-b border-background-200/80">
                <h2 className="text-[15px] font-bold text-foreground-950">热度推荐</h2>
                <span className="font-label text-[11px] tracking-[0.18em] text-foreground-400">
                  TRENDING
                </span>
              </div>
              <div className="p-3 space-y-2">
                {recommended.map((item) => (
                  <Link
                    key={item.id}
                    to={`/detail/${item.id}`}
                    className="group block rounded-md bg-background-100/70 p-3 hover:bg-primary-50 transition-colors cursor-pointer"
                  >
                    <p className="text-[13px] leading-6 font-medium text-foreground-800 group-hover:text-primary-700 transition-colors line-clamp-2-safe">
                      {item.title}
                    </p>
                    <p className="mt-1 text-[11px] text-foreground-400">{item.category}</p>
                  </Link>
                ))}
              </div>
            </section>
          </aside>
        </div>
      </div>
    </PageLayout>
  );
}